import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { DPSLayout } from '../../components/dps/layout/DPSLayout';
import { DPSSidebar } from '../../components/dps/layout/DPSSidebar';
import { VulnerabilityCard } from '../../components/dps/analysis/VulnerabilityCard';
import { RiskAnalysisCard } from '../../components/dps/analysis/RiskAnalysisCard';
import { dpsTheme } from '../../styles/dpsTheme';
import { pdf } from '@react-pdf/renderer';
import PDFDocument from '../../components/results/PDFDocument';
import toast from 'react-hot-toast';
import { detectVulnerabilities } from '../../utils/vulnerabilityDetection';
import { getCrimeStatistics, getCrimeComparison, CrimeData, CrimeComparison } from '../../services/criminalityService';

export const DPSAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { userProfile } = useAuth();
  const [diagnostic, setDiagnostic] = useState<any>(null);
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('vulnerabilities');

  // Crime stats — either from saved diagnostic_data or fetched on demand
  const [crimeData, setCrimeData] = useState<CrimeData | null>(null);
  const [crimeComparison, setCrimeComparison] = useState<CrimeComparison | null>(null);
  const [loadingCrime, setLoadingCrime] = useState(false);

  const vulnerabilitiesRef = useRef<HTMLDivElement>(null);
  const riskRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDiagnostic = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('diagnostics')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setDiagnostic(data);

        // ── Crime stats ──────────────────────────────────────────────────────
        const dd = data?.diagnostic_data ?? {};

        if (dd.crimeData && dd.crimeComparison) {
          // Already saved in diagnostic_data (happy path)
          setCrimeData(dd.crimeData);
          setCrimeComparison(dd.crimeComparison);
        } else if (dd.codeInsee) {
          // Missing from DB (old diagnostic or race condition) — fetch on demand
          setLoadingCrime(true);
          try {
            const fetched = await getCrimeStatistics(dd.codeInsee, dd.city ?? '');
            if (fetched) {
              const comparison = await getCrimeComparison(
                fetched,
                dd.codeDepartement ?? dd.codeInsee.substring(0, 2)
              );
              setCrimeData(fetched);
              setCrimeComparison(comparison);

              // Backfill into diagnostic_data so next load is instant
              await supabase
                .from('diagnostics')
                .update({
                  diagnostic_data: {
                    ...dd,
                    crimeData: fetched,
                    crimeComparison: comparison,
                  },
                })
                .eq('id', id);
            }
          } catch (crimeErr) {
            console.error('Error fetching crime statistics on demand:', crimeErr);
          } finally {
            setLoadingCrime(false);
          }
        }

        const { data: actionsData } = await supabase
          .from('improvement_actions_history')
          .select('*')
          .eq('diagnostic_id', id)
          .order('priority', { ascending: true });

        if (actionsData) {
          setActions(actionsData);
        }
      } catch (error) {
        console.error('Error loading diagnostic:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDiagnostic();
  }, [id]);

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);

    const refs: Record<string, React.RefObject<HTMLDivElement>> = {
      vulnerabilities: vulnerabilitiesRef,
      risk: riskRef,
    };

    const ref = refs[sectionId];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadPDF = async () => {
    if (!diagnostic) return;

    try {
      const diagnosticData = diagnostic.diagnostic_data;
      if (!diagnosticData) {
        console.error('Diagnostic data is missing');
        return;
      }

      const blob = await pdf(
        <PDFDocument
          formData={diagnosticData}
          improvementActions={actions}
          companyName={userProfile?.company_name}
          companyLogoUrl={userProfile?.company_logo_url ?? undefined}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DPS-${diagnosticData.city || 'rapport'}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  if (loading) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
        </div>
      </DPSLayout>
    );
  }

  if (!diagnostic) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Diagnostic non trouvé</div>
        </div>
      </DPSLayout>
    );
  }

  // Support both new and old format with fallback
  const categoryScores = diagnostic.category_scores || {
    physicalProtection: diagnostic.score_protection || 0,
    surveillance: diagnostic.score_surveillance || 0,
    deterrence: diagnostic.score_dissuasion || 0,
  };
  const diagnosticData = diagnostic.diagnostic_data || {};

  /**
   * Détection intelligente des vulnérabilités avec contexte Sprint 1
   */
  const vulnerabilities = detectVulnerabilities(diagnosticData, categoryScores);

  return (
    <DPSLayout reportId={id || ''} onDownloadPDF={handleDownloadPDF}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 lg:gap-8">
          <div className="hidden lg:block">
            <DPSSidebar activeSection={activeSection} onSectionClick={handleSectionClick} />
          </div>

          <main className="flex flex-col gap-6 lg:gap-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                Analyse approfondie de votre sécurité
              </h1>
              <p className="text-base sm:text-lg text-gray-400 mb-6 lg:mb-8">
                Vue détaillée de vos scores et recommandations personnalisées
              </p>
            </div>

            <section
              ref={vulnerabilitiesRef}
              id="vulnerabilities"
              className="backdrop-blur-xl border border-gray-700/50 rounded-lg p-4 sm:p-6 lg:p-8"
              style={{ background: dpsTheme.colors.background.card }}
            >
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Cartographie des vulnérabilités
              </h2>

              {vulnerabilities.length > 0 ? (
                vulnerabilities.map((vuln, index) => (
                  <VulnerabilityCard
                    key={index}
                    title={vuln.title}
                    description={vuln.description}
                    recommendations={vuln.recommendations}
                    severity={vuln.severity}
                  />
                ))
              ) : (
                <div
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${dpsTheme.colors.accent.green.border}`,
                    borderRadius: dpsTheme.borderRadius.md,
                    padding: dpsTheme.spacing.xl,
                    textAlign: 'center',
                  }}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={dpsTheme.colors.accent.green.primary}
                    strokeWidth="2"
                    style={{ margin: '0 auto 16px' }}
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <div
                    style={{
                      fontSize: dpsTheme.typography.fontSize.xl,
                      fontWeight: dpsTheme.typography.fontWeight.semibold,
                      color: dpsTheme.colors.accent.green.primary,
                      marginBottom: dpsTheme.spacing.sm,
                    }}
                  >
                    Aucune vulnérabilité majeure détectée
                  </div>
                  <p style={{ fontSize: dpsTheme.typography.fontSize.base, color: dpsTheme.colors.text.tertiary }}>
                    Votre logement présente un bon niveau de sécurité global. Continuez à maintenir vos équipements en bon état et restez vigilant.
                  </p>
                </div>
              )}
            </section>

            <section
              ref={riskRef}
              id="risk"
              className="backdrop-blur-xl border border-gray-700/50 rounded-lg p-4 sm:p-6 lg:p-8"
              style={{ background: dpsTheme.colors.background.card }}
            >
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Analyse du risque local
              </h2>

              <RiskAnalysisCard
                crimeData={crimeData ?? undefined}
                crimeComparison={crimeComparison ?? undefined}
                loading={loadingCrime}
              />
            </section>
          </main>
        </div>
      </div>
    </DPSLayout>
  );
};